import React, { useState } from "react";
import { Divider } from "@material-ui/core";
import useRouter from "use-react-router";

import { BoxOwner } from "../../../../dtos/box";
import { useAuth } from "../../../../features/auth/context";
import { PageHeader } from "../../../PageHeader";
import { SearchBar } from "../../../SearchBar";
import { Stack } from "../../../shared/Stack";
import { EnrollBiometricsDialog } from "../../../shared/EnrollBiometricsDialog";

import { BoxInfoCard } from "./BoxInfoCard";
import { BoxNotes } from "./BoxNotes";
import { BoxOwnerCard } from "./BoxOwnerCard";
import { BoxOwnersHeader } from "./BoxOwnersHeader";
import { LeaseDeleteButton } from "./LeaseDelete";
import { LeaseEndDateEditButton } from "./LeaseEndDateEdit";
import { LinkToBoxesPage } from "./LinkToBoxesPage";

import { BoxHistory } from "./BoxHistory";
import { useBox } from "./useBox";
import { useBoxOwners } from "./useBoxOwners";
import { ENABLED_FEATURE, useBoxFeatures } from "./useBoxFeatures";
import { filterBoxOwners } from "./utils";

export const BoxDetailPage = () => {
    const { email: userEmail } = useAuth();
    const { match } = useRouter();
    const id = (match.params as { boxId: number }).boxId;
    const { box, load: loadBox, loading: loadingBox } = useBox(id);
    const { owners, load: loadOwners, loading: loadingOwners } = useBoxOwners(id);
    const [ownerToEnroll, setOwnerToEnroll] = useState<BoxOwner | undefined>();
    const boxFeatures = useBoxFeatures(box, owners);
    const [searchText, setSearchText] = useState("");

    if (!box) {
        return (
            <Stack>
                <PageHeader isLoading={loadingBox || loadingOwners}>
                    Box #...
                </PageHeader>
                <LinkToBoxesPage />
            </Stack>
        );
    }

    return (
        <Stack>
            <PageHeader isLoading={loadingBox || loadingOwners}>
                Box #{box.number}
            </PageHeader>

            <BoxInfoCard box={box} owners={owners} />

            <BoxOwnersHeader
                box={box}
                owners={owners}
                onUpdate={() => {
                    loadBox();
                    loadOwners();
                }}
                boxFeatures={boxFeatures}
            />

            <Divider variant="fullWidth"></Divider>

            {owners.length === 0 && (
                <div>
                    To get started, add a new owner to this box
                </div>
            )}

            {owners.length > 0 && (<>
                <Stack direction="horizontal">
                    <LeaseEndDateEditButton
                        box={box}
                        onLeaseEndDateSaved={loadBox}
                        feature={boxFeatures.leaseEndDateEdit}
                    />
                    <LeaseDeleteButton
                        box={box}
                        onLeaseDeleted={() => {
                            loadBox();
                            loadOwners();
                        }}
                        feature={boxFeatures.leaseDelete}
                    />
                </Stack>

                <SearchBar
                    defaultSearchText={searchText}
                    search={setSearchText}
                    labelText="Search Owners..."
                    onReset={() => setSearchText("")}
                />

                {filterBoxOwners(owners, searchText).map((owner) => (
                    <BoxOwnerCard
                        key={owner.id}
                        owner={owner}
                        boxId={box.id}
                        onOwnerRemoved={() => {
                            loadBox();
                            loadOwners();
                        }}
                        onSetOwnerIdToEnroll={(id) => {
                            setOwnerToEnroll(
                                owners.find(it => it.id === id)
                            );
                        }}
                        ownerDeleteFeature={boxFeatures.ownerDelete}
                        ownerEnrollFeature={
                            owner.email === userEmail
                                ? ENABLED_FEATURE
                                : boxFeatures.ownerEnroll
                        }
                    />
                ))}
            </>)}

            <BoxNotes
                box={box}
                onNotesSaved={loadBox}
                feature={boxFeatures.boxNoteEdit}
            />

            <BoxHistory box={box} />

            <LinkToBoxesPage />
            <EnrollBiometricsDialog
                key={`${ownerToEnroll}`}
                user={ownerToEnroll}
                orgId={box.orgId}
                siteId={box.siteId}
                onClose={() => setOwnerToEnroll(undefined)}
                enrollmentFor={box.isBankBox ? "employee" : "customer"}
                boxIsExpired={!box.isBankBox && box.isExpired}
            />
        </Stack>
    );
};
